// sort-imports-ignore
import { DefaultSeo } from 'next-seo'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { AppProviders } from '~/src/components/providers/AppProviders'
import {
  inter_display_font,
  mono_font,
  sans_font,
  serif_font,
} from '~/src/fonts'
import { serializeJsonLd } from '~/src/lib/reports/jsonLd'
import { organizationJsonLd, websiteJsonLd } from '~/src/lib/seo/structuredData'
import DEFAULT_SEO from '~/src/next-seo.config'

// CSS imports
import '~/public/vendor/smartbanner/smartbanner.css'
import '~/src/styles/globals.css'
import 'streamdown/styles.css'

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter()

  const canonicalPath = router.asPath.split('?')[0]?.split('#')[0] ?? ''
  // A fallback or pre-hydration render of a dynamic route has the route
  // pattern as its asPath (`/chats/[hash]/check`), which is not a real URL.
  const canonical = canonicalPath.includes('[')
    ? undefined
    : canonicalPath === '/'
      ? 'https://dupe.com'
      : `https://dupe.com${canonicalPath}`

  return (
    <>
      <style jsx global>{`
        :root {
          --font-sans: ${sans_font.style.fontFamily};
          --font-display: ${inter_display_font.style.fontFamily};
          --font-serif: ${serif_font.style.fontFamily};
          --font-mono: ${mono_font.style.fontFamily};
        }
      `}</style>

      <DefaultSeo {...DEFAULT_SEO} canonical={canonical} />

      {/* JSON-LD, in `next/head` so it is SERVER-RENDERED.
       *
       * Must not be `next/script`: at its default `afterInteractive` strategy
       * Next injects the tag from the client after hydration, so the markup
       * never appears in the HTML document. Structured data that only exists
       * after JS has run is invisible to exactly the consumers it is for —
       * crawlers and AI agents reading the raw response. The WebSite block
       * below was `next/script` and was, as a result, absent from production
       * HTML entirely; only the next-seo-rendered Organization block was
       * making it out. Both are emitted through Head now.
       *
       * Organization serializes the SHARED object rather than re-declaring the
       * fields as next-seo `<OrganizationJsonLd>` props: the App Router layout
       * renders the same object, and next-seo has no prop for several fields we
       * emit (contactPoint[], legalName, address), so keeping the component
       * here would have guaranteed the two routers drift.
       */}
      <Head>
        <script
          id='jsonld-organization'
          type='application/ld+json'
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(organizationJsonLd),
          }}
        />
        {/* WebSite + SearchAction (Google sitelinks search box) */}
        <script
          id='jsonld-website'
          type='application/ld+json'
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(websiteJsonLd),
          }}
        />
      </Head>

      <AppProviders hash={pageProps?.hash}>
        <Component {...pageProps} />
      </AppProviders>
    </>
  )
}
